/**
 * Transformações Cloudinary on-the-fly para imagens de produto.
 *
 * Hosts sem CDN de transformação (iili.io / freeimage.host, postimg, uploads
 * locais) são devolvidos sem alteração — não inventamos parâmetros nessas URLs.
 */

export const CATALOG_SRCSET_WIDTHS = [250, 500, 800];
export const DETAIL_SRCSET_WIDTHS = [400, 800, 1200];
export const THUMB_SRCSET_WIDTHS = [80, 160];

export const CATALOG_IMAGE_SIZES = '(max-width: 640px) 50vw, 250px';
export const DETAIL_IMAGE_SIZES = '(max-width: 768px) 100vw, 800px';

const UPLOAD_MARKER = '/upload/';
const EMPERC_TRANSFORM_PREFIX = /^w_\d+,c_limit,f_auto,q_auto\//;

export function isCloudinaryUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const host = new URL(url).hostname.toLowerCase();
    return host === 'res.cloudinary.com' || host.endsWith('.cloudinary.com');
  } catch {
    return false;
  }
}

function buildTransform({ width, crop = 'limit', format = 'auto', quality = 'auto' }) {
  const safeWidth = Math.max(1, Math.round(Number(width) || 0));
  return `w_${safeWidth},c_${crop},f_${format},q_${quality}`;
}

/**
 * Insere (ou substitui) o bloco de transformação Emperc após /upload/.
 * Não altera public_ids com underscore (ex.: EAX67146203_ugls1n.avif).
 */
export function injectCloudinaryTransforms(url, transform) {
  if (!url || !transform) return url;
  const index = url.indexOf(UPLOAD_MARKER);
  if (index === -1) return url;

  const prefix = url.slice(0, index + UPLOAD_MARKER.length);
  const rest = url.slice(index + UPLOAD_MARKER.length).replace(EMPERC_TRANSFORM_PREFIX, '');
  return `${prefix}${transform}/${rest}`;
}

export function getCloudinaryTransformedUrl(url, options = {}) {
  if (!isCloudinaryUrl(url) || !options.width) return url;
  return injectCloudinaryTransforms(url, buildTransform(options));
}

export function getCloudinarySrcSet(url, widths = CATALOG_SRCSET_WIDTHS) {
  if (!isCloudinaryUrl(url) || !Array.isArray(widths) || widths.length === 0) {
    return '';
  }
  return widths
    .map((width) => `${getCloudinaryTransformedUrl(url, { width })} ${width}w`)
    .join(', ');
}

/**
 * URL de exibição: Cloudinary ganha w_*, demais hosts permanecem iguais.
 */
export function getOptimizedImageSrc(url, { width } = {}) {
  if (!url) return url;
  if (isCloudinaryUrl(url) && width) {
    return getCloudinaryTransformedUrl(url, { width });
  }
  return url;
}

export function getResponsiveImageProps(
  url,
  { widths = CATALOG_SRCSET_WIDTHS, fallbackWidth } = {}
) {
  if (!url) {
    return { src: url, srcSet: undefined, canOptimize: false };
  }

  if (!isCloudinaryUrl(url)) {
    return { src: url, srcSet: undefined, canOptimize: false };
  }

  const primaryWidth =
    fallbackWidth || widths[Math.min(1, widths.length - 1)] || widths[0];

  return {
    src: getCloudinaryTransformedUrl(url, { width: primaryWidth }),
    srcSet: getCloudinarySrcSet(url, widths),
    canOptimize: true,
  };
}

export function getLcpPreloadAttrs(
  url,
  {
    widths = CATALOG_SRCSET_WIDTHS,
    sizes = CATALOG_IMAGE_SIZES,
    width = 500,
  } = {}
) {
  if (!url) return null;
  const props = getResponsiveImageProps(url, { widths, fallbackWidth: width });
  return {
    href: props.src,
    imageSrcSet: props.srcSet || undefined,
    imageSizes: props.srcSet ? sizes : undefined,
  };
}
